/**
 * Execution block: writes a value to a named property on a SceneObject's
 * Properties behavior. The target must have a Properties behavior that
 * defines the named property — no ancestor walk for the write target.
 *
 * On success, Properties.setValue() notifies all $ref subscribers and
 * cascades to descendant Properties, so the entire reactive binding
 * system updates automatically.
 *
 * If the target has no Properties behavior or doesn't define the property,
 * the block logs a warning and continues (no-op).
 */

import { FlowGraphExecutionBlockWithOutSignal } from "@babylonjs/core/FlowGraph/flowGraphExecutionBlockWithOutSignal";
import type { FlowGraphContext } from "@babylonjs/core/FlowGraph/flowGraphContext";
import type { FlowGraphSignalConnection } from "@babylonjs/core/FlowGraph/flowGraphSignalConnection";
import type { IFlowGraphBlockConfiguration } from "@babylonjs/core/FlowGraph/flowGraphBlock";
import { RichTypeAny } from "@babylonjs/core/FlowGraph/flowGraphRichTypes";
import { Vector3, Quaternion } from "@babylonjs/core/Maths/math.vector";
import { resolveTarget } from './traceContext';
import type { PropertyValue } from '../../tracebehavior';
import type { TimeTransition } from '../../../scenedata/behaviors/propertiesdata';
import { Logger } from '../../../utils/Logger';

export class TraceSetPropertyBlock extends FlowGraphExecutionBlockWithOutSignal {
    private _targetPath: string;
    private _propertyName: string;
    private _transition: TimeTransition | undefined;

    constructor(config?: IFlowGraphBlockConfiguration & { target?: string; property?: string; transition?: TimeTransition }) {
        super(config);
        this._targetPath = config?.target ?? '';
        this._propertyName = config?.property ?? '';
        this._transition = config?.transition;
        this.registerDataInput("value", RichTypeAny);
    }

    public override _execute(context: FlowGraphContext, _callingSignal: FlowGraphSignalConnection): void {
        const target = resolveTarget(context, this._targetPath);
        if (target) {
            const props = target.getBehavior('Properties');
            if (props) {
                let value: unknown = this.getDataInput("value")!.getValue(context);

                // Graph math runs on Babylon vector instances (pointer/get,
                // math/*), but property values are plain arrays — convert so
                // "copy an object's position into a vec3 property" type-checks
                if (value instanceof Vector3 || value instanceof Quaternion) {
                    value = value.asArray();
                }

                // Auto-convert vec3 [r,g,b] to hex color string when targeting a color property
                const propDef = props.getProperty(this._propertyName);
                if (propDef?.type === 'color' && Array.isArray(value) && value.length >= 3) {
                    const toHex = (n: number) => Math.round(Math.max(0, Math.min(1, n)) * 255)
                        .toString(16).padStart(2, '0');
                    value = `#${toHex(value[0] as number)}${toHex(value[1] as number)}${toHex(value[2] as number)}`;
                }

                // undefined transition → the resolver's declaration-default fallback fires
                const accepted = props.setValue(this._propertyName, value as PropertyValue, this._transition);
                if (!accepted) {
                    Logger.warn(`[TraceSetPropertyBlock] Property "${this._propertyName}" not found or type mismatch on "${target.name}"`);
                }
            } else {
                Logger.warn(`[TraceSetPropertyBlock] Target "${target.name}" has no Properties behavior`);
            }
        }
        this.out._activateSignal(context);
    }

    public override getClassName(): string {
        return "TraceSetPropertyBlock";
    }
}
